import { mergeProps, useObjectRef } from "@react-aria/utils";
import { clsx } from "clsx";
import { type ForwardedRef, type ReactNode, useRef } from "react";
import {
  type AriaCheckboxProps,
  type AriaGridListItemOptions,
  type AriaGridListOptions,
  useCheckbox,
  useFocusRing,
  useGridList,
  useGridListItem,
  useGridListSelectionCheckbox,
  useHover,
  VisuallyHidden,
} from "react-aria";
import { type ListProps, type ListState, type Node, useListState, useToggleState } from "react-stately";

import { forwardRef } from "../../utils/jsx/forwardRef.ts";
import styleMenu from "../ContextualMenu/ContextualMenu.module.css";
import { HStack } from "../Stack/HStack.tsx";

type GridListProps<T> = AriaGridListOptions<T> & ListProps<T>;

const GridListRender = <T extends object>(
  props: GridListProps<T>,
  forwardRef: ForwardedRef<HTMLUListElement>,
): ReactNode => {
  const ref = useObjectRef(forwardRef);
  const state = useListState(props);
  const { gridProps } = useGridList(props, state, ref);

  return (
    <ul {...gridProps} className={styleMenu.list} ref={ref} style={{ margin: 0 }}>
      {[...state.collection].map((node) => (
        <GridLisItem key={node.key} node={node} isVirtualized={props.isVirtualized} state={state} />
      ))}
    </ul>
  );
};

interface GridLisItemProps<T> extends AriaGridListItemOptions {
  state: ListState<T>;
}

const GridLisItem = <T extends object>({ state, ...props }: GridLisItemProps<T>) => {
  const ref = useRef<HTMLLIElement>(null);
  const { rowProps, gridCellProps, isFocused, isSelected, isDisabled } = useGridListItem(props, state, ref);

  const { focusProps } = useFocusRing();
  const showCheckbox =
    state.selectionManager.selectionMode !== "none" && state.selectionManager.selectionBehavior === "toggle";
  const { hoverProps, isHovered } = useHover({ isDisabled });

  return (
    <li
      {...mergeProps(rowProps, hoverProps, focusProps)}
      ref={ref}
      className={clsx(styleMenu.item, styleMenu.item_with_border, {
        [styleMenu.item_disabled]: isDisabled,
        [styleMenu.item_focus]: isFocused || isHovered,
        [styleMenu.intent_selected]: isSelected,
      })}
      style={{
        display: "block",
        width: "100%",
      }}
    >
      <HStack domProps={gridCellProps} style={{ flex: "1 1 auto" }}>
        {!!showCheckbox && <ListCheckbox node={props.node as Node<object>} state={state} />}
        {!!showCheckbox && <Checkbox onChange={console.log} />}
        {!!showCheckbox && <CheckboxSimple onChange={console.log} />}

        {props.node.rendered}
      </HStack>
    </li>
  );
};

interface ListCheckboxProps<T> {
  state: ListState<T>;
  node: Node<T>;
}

const ListCheckbox = <T extends object>({ state, node }: ListCheckboxProps<T>) => {
  const { checkboxProps } = useGridListSelectionCheckbox({ key: node.key }, state);

  return <Checkbox {...checkboxProps} />;
};

function CheckboxSimple(props: AriaCheckboxProps) {
  const inputRef = useRef(null);
  const { inputProps } = useCheckbox(props, useToggleState(props), inputRef);
  return <input {...inputProps} ref={inputRef} />;
}

function Checkbox(props: AriaCheckboxProps) {
  const state = useToggleState(props);
  const ref = useRef(null);
  const { inputProps } = useCheckbox(props, state, ref);
  const { isFocusVisible, focusProps } = useFocusRing();
  const isSelected = state.isSelected && !props.isIndeterminate;

  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        opacity: props.isDisabled ? 0.4 : 1,
      }}
    >
      <VisuallyHidden>
        <input {...inputProps} {...focusProps} ref={ref} />
      </VisuallyHidden>
      <svg width={24} height={24} aria-hidden="true" style={{ marginRight: 4 }}>
        <rect
          x={isSelected ? 4 : 5}
          y={isSelected ? 4 : 5}
          width={isSelected ? 16 : 14}
          height={isSelected ? 16 : 14}
          fill={isSelected ? "orange" : "none"}
          stroke={isSelected ? "none" : "gray"}
          strokeWidth={2}
        />
        {!!isSelected && (
          <path
            transform="translate(7 7)"
            d={`M3.788 9A.999.999 0 0 1 3 8.615l-2.288-3a1 1 0 1 1
            1.576-1.23l1.5 1.991 3.924-4.991a1 1 0 1 1 1.576 1.23l-4.712
            6A.999.999 0 0 1 3.788 9z`}
          />
        )}
        {!!props.isIndeterminate && <rect x={7} y={11} width={10} height={2} fill="gray" />}
        {!!isFocusVisible && <rect x={1} y={1} width={22} height={22} fill="none" stroke="orange" strokeWidth={2} />}
      </svg>
      {props.children}
    </label>
  );
}

export const GridList = forwardRef(GridListRender);
